import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plus,
  Eye,
  Pencil,
  Trash2,
  ArrowUpRightFromSquare,
  Wallet,
  Tags,
  ListChecks,
  BarChart3,
} from "lucide-react";
import { supabase } from "../../../Services/supabaseClient";
import { useCompany } from "../../../context/CompanyContext";
import "./fixedAssets.css";
import { formatNaira } from "./faMoney";
import PostDepreciationModal from "./PostDepreciationModal";
import FixedAssetCSVImport from "./FixedAssetCSVImport";

export default function FixedAssetsList() {
  const { companyId, companyRole } = useCompany();
  const nav = useNavigate();

  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);

  const [q, setQ] = useState("");
  const [status, setStatus] = useState("ALL");

  const [showPostDep, setShowPostDep] = useState(false);

  async function fetchAssets() {
    if (!companyId) return;
    setLoading(true);

    const { data, error } = await supabase
      .from("fixed_assets")
      .select("id, asset_tag, name, category, cost, status, in_service_date")
      .eq("company_id", companyId)
      .order("created_at", { ascending: false });

    if (!error) setAssets(data || []);
    setLoading(false);
  }

  useEffect(() => {
    fetchAssets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const filtered = useMemo(() => {
    const qq = q.trim().toLowerCase();
    return (assets || []).filter((a) => {
      const okStatus = status === "ALL" ? true : a.status === status;
      const okQ =
        !qq ||
        (a.asset_tag || "").toLowerCase().includes(qq) ||
        (a.name || "").toLowerCase().includes(qq) ||
        (a.category || "").toLowerCase().includes(qq);
      return okStatus && okQ;
    });
  }, [assets, q, status]);

  async function deleteAsset(id) {
    const yes = window.confirm(
      "Delete this asset? This does NOT reverse any posted journals."
    );
    if (!yes) return;

    const { error } = await supabase
      .from("fixed_assets")
      .delete()
      .eq("id", id)
      .eq("company_id", companyId);
    if (error) {
      alert(error.message);
      return;
    }
    fetchAssets();
  }

  return (
    <div className="fa-wrap">
      <div className="fa-header">
        <div className="fa-title">
          <h2>Fixed Assets</h2>
          <p>
            Manage your asset register, post depreciation, and handle disposals.
            Journals are posted to the ledger.
          </p>
        </div>

        <div className="fa-actions">
          <button className="fa-btn" onClick={() => setShowPostDep(true)}>
            <Wallet size={16} /> Post Depreciation
          </button>
          <button
            className="fa-btn"
            onClick={() => nav("/fixed-assets/fixed-asset-depreciation-runs")}
          >
            <ListChecks size={16} /> Runs
          </button>
          <button
            className="fa-btn"
            onClick={() => nav("/fixed-assets/fixed-asset-rollforward")}
          >
            <BarChart3 size={16} /> Roll-forward
          </button>

          <button
            className="fa-btn fa-btn-primary"
            onClick={() => nav("/fixed-assets/new")}
          >
            <Plus size={16} /> Add Asset
          </button>
          <FixedAssetCSVImport
            companyId={companyId}
            onImported={() => {
              // optional: route user back to the list or just toast/alert
              alert("Import completed. Check your Fixed Assets list.");
              fetchAssets();
            }}
          />

          <button
            className="fa-btn"
            onClick={() => nav("/fixed-assets/fixed-asset-categories")}
          >
            <Tags size={16} /> Categories
          </button>
        </div>
      </div>

      <div className="fa-panel">
        <div className="fa-toolbar">
          <input
            className="fa-input"
            placeholder="Search tag, name, category…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <select
            className="fa-select"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            <option value="ALL">All statuses</option>
            <option value="ACTIVE">Active</option>
            <option value="DRAFT">Draft</option>
            <option value="DISPOSED">Disposed</option>
            <option value="WRITTEN_OFF">Written off</option>
          </select>
        </div>

        {loading ? (
          <div style={{ opacity: 0.8, padding: 10 }}>Loading…</div>
        ) : (
          <table className="fa-table">
            <thead>
              <tr>
                <th>Tag</th>
                <th>Asset</th>
                <th>Category</th>
                <th>In service</th>
                <th>Cost</th>
                <th>Status</th>
                <th style={{ width: 160 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((a) => (
                <tr key={a.id}>
                  <td>{a.asset_tag}</td>
                  <td>{a.name}</td>
                  <td>{a.category || "-"}</td>
                  <td>{a.in_service_date}</td>
                  <td>{formatNaira(a.cost)}</td>
                  <td>
                    <span
                      className={`fa-badge ${
                        a.status === "ACTIVE" ? "fa-badge-active" : ""
                      }`}
                    >
                      {a.status}
                    </span>
                  </td>
                  <td>
                    <div className="fa-row-actions">
                      <button
                        className="fa-icon-btn"
                        onClick={() =>
                          nav(`/fixed-assets/fixed-assets/${a.id}`)
                        }
                        title="View"
                      >
                        <Eye size={16} />
                      </button>
                      <button
                        className="fa-icon-btn"
                        onClick={() =>
                          nav(`/fixed-assets/fixed-assets/${a.id}/edit`)
                        }
                        title="Edit"
                      >
                        <Pencil size={16} />
                      </button>
                      {companyRole === "admin" && (
                        <button
                          className="fa-icon-btn"
                          onClick={() => deleteAsset(a.id)}
                          title="Delete (admin only)"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}

                      <button
                        className="fa-icon-btn"
                        onClick={() =>
                          nav(`/fixed-assets/fixed-assets/${a.id}`)
                        }
                        title="Open"
                      >
                        <ArrowUpRightFromSquare size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={7} style={{ opacity: 0.75 }}>
                    No assets found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {showPostDep && (
        <PostDepreciationModal
          companyId={companyId}
          onClose={() => setShowPostDep(false)}
          onPosted={() => {
            setShowPostDep(false);
            fetchAssets(); // or whatever refresh function exists there
          }}
        />
      )}
    </div>
  );
}
